<template>
  <div>
    <a-table
      :columns="columns"
      :data-source="channelsCustomer"
      :loading="loading"
      row-key="id"
      size="middle"
      :pagination="false"
      :scroll="scrollStyle"
    >
      <template #headerCell="{ column, title }">
        <template v-if="column.info">
          {{ title }}

          <a-tooltip :title="column.info">
            <InfoCircleOutlined />
          </a-tooltip>
        </template>
      </template>

      <template #channel="{ record, text }">
        <div class="channel">
          <div
            v-if="record.channel_icon"
            class="channel__logo"
            :style="`background-image: url('${record.channel_icon}')`"
          />
          <div v-else class="channel__logo channel__logo--empty" />
          <span>{{ formatName(text) }} </span>
        </div>
      </template>

      <template #associate="{ record }">
        <span style="display: flex; align-items: center">
          <span style="max-width: calc(100% - 25px); overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
            {{ showAssociated(record) }}
          </span>

          <a-tooltip title="Copiar" v-if="isWebChat(record)">
            <CopyOutlined
              class="copy-icon"
              v-clipboard:copy="showAssociated(record)"
              v-clipboard:success="handleCopySuccess"
              v-clipboard:error="handleCopyError"
              style="margin-left: 6px"
            />
          </a-tooltip>

          <a-popover
            v-if="record.channel_id === ChannelType.Email"
            placement="bottomLeft"
            trigger="hover"
            :overlayStyle="{ width: '250px' }"
          >
            <template #content>
              <div class="send-as-popover-content">
                <div class="send-as-title" v-if="sendAsAliasesCache[record.id]">
                  Enviar como ({{ sendAsAliasesCache[record.id].length }})
                </div>
                <div class="send-as-title" v-else>
                  Enviar como (0)
                </div>
                
                <ul class="send-as-list" v-if="sendAsAliasesCache[record.id] && sendAsAliasesCache[record.id].length > 0">
                  <li 
                    v-for="alias in sendAsAliasesCache[record.id]" 
                    :key="alias.id"
                    class="send-as-item"
                  >
                    <span class="bullet">•</span>
                    <span :title="alias.email" class="email-text">
                      {{ alias.email }}
                    </span>
                  </li>
                </ul>
                <div v-else-if="sendAsAliasesCache[record.id] === undefined || sendAsAliasesCache[record.id] === null" style="padding: 4px 0">
                  Cargando...
                </div>
                <div v-else style="padding: 4px 0; color: rgba(0, 0, 0, 0.45)">
                  Sin correos configurados
                </div>
              </div>
            </template>
            <InfoCircleOutlined 
              class="info-icon-hover" 
              style="margin-left: 6px; color: rgba(0, 0, 0, 0.45); cursor: pointer;" 
              @mouseenter="loadPopoverAliases(record)"
            />
          </a-popover>
        </span>
      </template>

      <template #alias="{ record, text }">
        <span v-if="text">{{ text }}</span>
        <template v-else>
          <span v-if="record.channel_id === ChannelType.VoiceInbound" class="info">
            Sin alias

            <a-tooltip
              title="Recuerda agregar un alias a este número para facilitar su identificación en cualquier módulo."
            >
              <InfoCircleOutlined style="color: #fa8c16; margin-left: 4px" />
            </a-tooltip>
          </span>

          <span v-else> - </span>
        </template>
      </template>

      <template #adderUser="{ record: { data_parameters } }">
        <div>
          <span> {{ data_parameters?.sc_user?.fullname || "-" }} </span>

          <a-popover
            :overlayStyle="{ width: '230px' }"
            placement="right"
            v-if="data_parameters?.fb_user"
          >
            <template #title> Cuenta de Facebook con la que se conectó la página </template>

            <template #content>
              {{ data_parameters?.fb_user?.name }}
            </template>

            <a-button class="button__table" type="link">
              Ver su usuario en
              <img :src="facebookLogoImage" alt="Facebook Logo" />
            </a-button>
          </a-popover>
        </div>
      </template>

      <template #createdAtTitle>
        Fecha de creación
        <a-tooltip>
          <template #title> Ordenar </template>
          <SwapOutlined :rotate="90" />
        </a-tooltip>
      </template>
      <template #createdAt="{ text }">
        <span>
          {{ dateTimeToSlashFormat(text, false) }}
        </span>
      </template>

      <template #statusTitle>
        Estado
        <a-tooltip>
          <template #title> Ordenar </template>
          <SwapOutlined :rotate="90" />
        </a-tooltip>
      </template>
      <template #status="{ record }">
        <a-tag :color="statusColor(record)" style="margin-inline-end: 0;">{{ status(record) }}</a-tag>
        <span v-if="record.status === channelStatus.inactive && record.channel_id === ChannelType.WhatsappVoice && isBlockByWhatsappVoiceBalance()">  
          <a-tooltip
            title="Canal inactivado por saldo insuficiente. Se pausaron sus configuraciones. Recarga para activarlo y retomar su actividad."
          >
            <WarningOutlined style="color: #FAAD14; margin-left: 4px" />
          </a-tooltip>
        </span>
        <span v-if="record.status === channelStatus.incomplete">
          <a-tooltip
            title="La solicitud estará Incompleta hasta finalizar la configuración en Facebook y pase a Pendiente de aprobación por Meta."
          >
            <InfoCircleOutlined style="color: #00000073; margin-left: 4px" />
          </a-tooltip>
        </span>
      </template>
      <template #actionScoped="{ record }">
        <div style="display: flex; gap: 16px; align-items: center">
          <!-- Unlink -->
          <div v-if="isFacebook(record)">
            <a-button
              v-if="isChannelActive(record)"
              type="link"
              size="small"
              @click="handleUnLinkFromFacebook(record)"
            >
              <img src="@/app/channels/components/image/desvincular.svg" alt="unlink" />
            </a-button>
            <a-button v-else type="link" size="small" @click="handleLinkWithFacebook(record)">
              <img src="@/app/channels/components/image/vincular.svg" alt="link" />
            </a-button>
          </div>

          <!-- Active or inactive -->
          <a-tooltip
            v-else
            :title="record.status === channelStatus.incomplete
              ? 'Este identificador se activará automáticamente cuando sea aprobado por Meta.'
              : ''"
          >
            <span>
              <a-switch
                :disabled="isSMS(record) || (isInbound(record) && record.status === channelStatus.pending) || record.status === channelStatus.incomplete"
                :checked="isChannelActive(record)"
                class="switch-status"
                size="small"
                @change="handleStatusChange(record)"
              />
            </span>
          </a-tooltip>

          <a-popover placement="topRight">
            <template #content>
              <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 8px">
                <!-- Edit -->
                <a-button
                  v-if="allowEdit(record) && (record.status === channelStatus.active || record.status === channelStatus.inactive)"
                  type="text"
                  class="button"
                  @click="handleEdit(record)"
                >
                  <EditOutlined />

                  Editar identificador
                </a-button>

                <!-- Configurar Enviar como -->
                <a-tooltip
                  v-if="record.channel_id === ChannelType.Email && (record.status === channelStatus.active || record.status === channelStatus.inactive)"
                  :title="isSendAsDisabled(record)
                    ? 'Activa el identificador para habilitar la acción Configurar &quot;Enviar como&quot;'
                    : 'Configura el correo que verán tus contactos como remitente, sin modificar el identificador desde el que se envía el mensaje.'"
                  placement="left"
                >
                  <span style="display: inline-block; width: 100%;">
                    <a-button
                      type="text"
                      class="button"
                      :disabled="isSendAsDisabled(record)"
                      @click="handleSendAsConfig(record)"
                      style="width: 100%; text-align: left;"
                    >
                      <SettingOutlined />

                      Configurar "Enviar como"
                    </a-button>
                  </span>
                </a-tooltip>

                <!-- Information -->
                <a-button
                  type="text"
                  class="button"
                  v-if="[channelStatus.pending, channelStatus.reject, channelStatus.incomplete].includes(record.status)"
                  @click="handleInfo(record)"
                >
                  <EyeOutlined />

                  Ver solicitud
                </a-button>

                <!-- WhatsappAutoSetup Configuration  -->
                <a-button
                  type="text"
                  class="button"
                  v-if="record.status === channelStatus.incomplete"
                  @click="handleWhatsappConfiguration(record)"
                >
                  <WhatsAppOutlined />

                  Completar configuración
                </a-button>

                <!-- Whatsapp Templates -->
                <a-button
                  v-if="(isWhatsapp(record) || isSMS(record)) && (record.status === channelStatus.active || record.status === channelStatus.inactive)"
                  class="button"
                  type="text"
                  @click="goToAddTemplate(record)"
                >
                  <FileAddOutlined />

                  Agregar plantillas
                </a-button>

                <!-- Activar Whatsapp Voice -->
                <template v-if="ActiveWhatsappVoice(record)">
                  <!-- Channel activo -->
                  <a-button
                    v-if="record.status === channelStatus.active"
                    class="button"
                    type="text"
                    @click="handleEnableVoice(record)"
                    :class="{'btn-disabled': !plansWhatsappVoice}"
                  >
                    <WhatsAppOutlined />
  
                    Activar WhatsApp Voice
                  </a-button>

                  <!-- Channel inactivo -->
                  <a-tooltip
                    v-else
                    title="Es necesario activar el identificador de WhatsApp Message primero."
                    placement="right"
                  >
                  <span style="display:inline-block;">
                    <a-button
                      class="button"
                      type="text"
                      disabled
                    >
                      <WhatsAppOutlined />

                      Activar WhatsApp Voice
                    </a-button>
                  </span>
                  </a-tooltip>
                </template>

                <a-button
                  v-if="!record.data_parameters?.enable_mm_lite && isWhatsapp(record) && (record.status === channelStatus.active)"
                  class="button"
                  type="text"
                  @click="handleLinkWithWhatsApp(record)"
                >
                  <WhatsAppOutlined />
                  Validar MMLite
                </a-button>

                <!-- Delete -->

                <a-button
                  class="button"
                  type="text"
                  ghost
                  danger
                  v-if="(!isSMS(record) && (!isInbound(record) || isInbound(record) && record.status != channelStatus.pending)) 
                    || (isSMS(record) && (record.status === channelStatus.reject))"
                  @click="handleDeleteChannel(record)"
                >
                  <DeleteOutlined />

                  {{
                    record.status === channelStatus.reject
                    ? "Eliminar solicitud"
                    : (record.status === channelStatus.active || record.status === channelStatus.inactive) 
                      ? "Eliminar identificador"
                      : "Cancelar solicitud"
                  }}
                </a-button>
              </div>
            </template>

            <a-button type="text" style="padding: 0; height: 22px">
              <EllipsisOutlined style="font-size: 16px" :rotate="90" />
            </a-button>
          </a-popover>
        </div>
      </template>
    </a-table>
    <edit-whatsapp
      v-if="whatsappModal"
      :open="whatsappModal"
      :record="recordWhatsapp"
      @close="whatsappModal = false"
      @update="handleReloadTable"
    />

    <ChannelInfo v-if="infoRecord" v-model:visible="visibleInfoModal" :record="infoRecord" />

    <WebChatChannel
      :open="webChatModal"
      :isEdit="true"
      :channelData="recordInbound"
      @close="webChatModal = false"
      v-if="recordInbound && webChatModal"
      @update="handleReloadTable"
    />

    <voice-channel
      v-if="voiceModal"
      :open="voiceModal"
      :isEdit="true"
      :record="recordVoice"
      @close="voiceModal = false"
      @update="handleReloadTable"
    />

    <deactivate-channel
      :record="recordDeactivate"
      :visible="visibleDeactivate"
      @reloadTable="handleReloadTable"
      @onClose="visibleDeactivate = false"
    />

    <delete-channel
      :record="recordDelete"
      v-if="visibleDelete"
      :visible="visibleDelete"
      @reloadTable="handleReloadTable"
      @onClose="visibleDelete = false"
    />
    <EmailChannel
      v-if="emailModal"
      :isEdit="true"
      :data="emailRecord"
      class="setup-modal"
      :open="emailModal"
      @reloadTable="handleReloadTable"
      @close="emailModal = false"
    />

    <SendAsConfigModal
      v-if="sendAsModalVisible"
      :open="sendAsModalVisible"
      :record="sendAsRecord"
      @close="handleSendAsModalClose"
    />

    <active-voice-modal
      :record="recordWhatsappVoice"
      v-if="visibleWhatsappVoice"
      :visible="visibleWhatsappVoice"
      @reloadTable="handleReloadTable"
      @onClose="visibleWhatsappVoice = false"
    />

    <cancel-channel
      :record="recordCancel"
      v-if="visibleCancel"
      :visible="visibleCancel"
      @reloadTable="handleReloadTable"
      @onClose="visibleCancel = false"
    />

    <RestrictionChangePlanModal
      :title="
        isClientBeexcc
          ? 'Realiza un upgrade y accede a todo el poder de Beex.'
          : 'Solicita un upgrade y accede a todo el poder de BeexCC.'
      "
      v-model:open="openRestriction"
    >
      <template #description>
        Tu plan {{ PlanTypeText[plan] }} no incluye el canal de <span style="font-weight: 500;">Voz</span>.
        Solicita un upgrade y potencia tu ambiente de trabajo. 
        ¡Que tu viaje no se detenga!
      </template>
    </RestrictionChangePlanModal>

    <WhatsappFacebookConfiguration
      ref="WhatsappFacebookConfigurationRef"
      :open="whatsappModalConfiguration"
      @close="whatsappModalConfiguration = false"
      @refresh="refreshWhatsappConfiguration"
    />
  </div>
</template>

<script>
import { defineComponent, createVNode } from "vue";
import { Modal, message } from "ant-design-vue";
import {
  SwapOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  CopyOutlined,
  FileAddOutlined,
  EllipsisOutlined,
  WhatsAppOutlined,
  WarningOutlined,
  SettingOutlined,
} from "@ant-design/icons-vue";
import { channelTablecolumns, channelStatus } from "@/app/channels/utils/constants";
import { fbSubscribeApps, fbUnlinkPage } from "@/app/channels/utils/facebookManager";
import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { ChannelType } from "@/app/shared/utils/enums";
import { TypeBilling } from "@/app/workspace/utils/enums";
import { fetchMmLite, fetchSendAsAliases } from "@/app/channels/services";
import { fetchWhatsappBalance } from "@/app/settings/services";
import { fetchValidateChannelBots } from "@/app/entities/services";
import EditWhatsapp from "@/app/channels/components/modals/whatsapp/EditWhatsapp.vue";
import WebChatChannel from "@/app/channels/components/modals/webChat/Index.vue";
import VoiceChannel from "@/app/channels/components/modals/voice/VoiceChannel.vue";
import DeactivateChannel from "@/app/channels/components/modals/DeactivateChannel.vue";
import DeleteChannel from "@/app/channels/components/modals/DeleteChannel.vue";
import ChannelInfo from "@/app/channels/components/modals/ChannelInfo.vue";
import EmailChannel from "@/app/channels/components/modals/email/EmailChannel.vue";
import ActiveVoiceModal from "@/app/channels/components/modals/whatsapp/ActiveVoiceModal.vue";
import CancelChannel from "@/app/channels/components/modals/CancelChannel.vue";
import SendAsConfigModal from "@/app/channels/components/modals/email/SendAsConfigModal.vue";

import facebookLogoImage from "@/app/channels/assets/facebook-logo.svg?url";
import { PlanType, PlanTypeText } from "@/app/common/types";
import RestrictionChangePlanModal from "@/app/common/components/modals/RestrictionChangePlanModal.vue";
import WhatsappFacebookConfiguration from "@/app/channels/components/modals/whatsapp/WhatsappFacebookConfiguration.vue";

export default defineComponent({
  name: "TableListChannels",
  components: {
    SwapOutlined,
    EditOutlined,
    CopyOutlined,
    EditWhatsapp,
    WebChatChannel,
    VoiceChannel,
    DeleteOutlined,
    DeactivateChannel,
    DeleteChannel,
    InfoCircleOutlined,
    EyeOutlined,
    FileAddOutlined,
    ChannelInfo,
    EmailChannel,
    EllipsisOutlined,
    WhatsAppOutlined,
    WarningOutlined,
    ActiveVoiceModal,
    CancelChannel,
    RestrictionChangePlanModal,
    WhatsappFacebookConfiguration,
    SettingOutlined,
    SendAsConfigModal,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { scrollStyle } = useScrollResize({ headerMargin: 240 });

    return { scrollStyle, ChannelType, channelStatus, facebookLogoImage, TypeBilling, PlanTypeText };
  },
  data() {
    return {
      columns: channelTablecolumns,
      facebook: 6,
      whatsappModal: false,
      whatsappModalConfiguration: false,
      webChatModal: false,
      voiceModal: false,
      visibleDeactivate: false,
      visibleDelete: false,
      visibleIframe: false,
      linkFrame: "",
      recordDeactivate: {},
      recordDelete: {},
      recordWhatsapp: {},
      recordVoice: {},
      recordInbound: {},
      dateTimeToSlashFormat,
      visibleInfoModal: false,
      infoRecord: undefined,
      emailModal: false,
      emailRecord: {},
      sendAsModalVisible: false,
      sendAsRecord: {},
      sendAsAliasesCache: {},
      visibleWhatsappVoice: false,
      recordWhatsappVoice: {},
      visibleCancel: false,
      recordCancel: {},
      balance_voice: 0,
      type_billing_voice: null,
      openRestriction: false,
    };
  },
  created() {
    this.fetchWhatsappVoiceBalance();
    this.preloadEmailAliases();
  },
  computed: {
    channelsCustomer() {
      // Muestra SMS
      return this.$store.getters["getChannelsCustomersList"];

      // Hidden SMS for PoC
      // return this.$store.getters["getChannelsCustomersList"].filter(
      //   (channel) => channel.channel_name !== "SMS"
      // );
    },
    userData() {
      return this.$store.getters["getDataUser"];
    },
    plan() {
      console.log(this.userData, 'this.userData');
      return this.userData?.change_custom_plan?.plan;
    },
    plansWhatsappVoice() {
      return (
        this.plan == PlanType.DigitalVoice || this.plan ==  PlanType.AllInOne || this.plan == 5 
      )
    },
    isClientBeexcc() {
      return this.userData?.is_beexcc;
    },
  },
  watch: {
    channelsCustomer(channelCustomersList) {
      this.preloadEmailAliases();

      const channelCustomerId = Number(this.$route.query["channel_customer_id"]);

      const channelToOpen = channelCustomersList.find(
        (channelCustomer) => channelCustomer.id === channelCustomerId
      );

      if (!channelToOpen) return;

      this.voiceModal = true;
      this.recordVoice = channelToOpen;
    },
  },
  methods: {
    isWhatsappMessagePlusVoice(record) {
      return (
        record.channel_id === ChannelType.Whatsapp &&
        record?.data_parameters?.enable_voice === true
      )
    },
    findMessageForVoice(record) {
      const voiceNum = record.associate;
      if (!voiceNum) return null;

      const list = this.channelsCustomer || [];
      return list.find(ch =>
        ch.channel_id === ChannelType.Whatsapp &&
        ch.data_parameters.phone_number === voiceNum &&
        ch.status === channelStatus.inactive
      ) || null;
    },
    formatName(name) {
      switch (name) {
        case "Telefonica":
          return "Voice";
        case "Messenger":
          return "Facebook Messenger";
        case "Chat Inbound":
          return "Web Chat";
        case "EMAIL":
          return "Correo";
        default:
          return name;
      }
    },
    statusColor(record) {
      if (record.status === channelStatus.active) {
        return "green";
      }
      if (record.status === channelStatus.inactive || record.status === channelStatus.reject) {
        return "red";
      }
      if (record.status === channelStatus.pending) {
        return "gold";
      }
      return "default";
    },
    status(record) {
      if (record.status === channelStatus.active) {
        return "Activo";
      }
      if (record.status === channelStatus.inactive) {
        return "Inactivo";
      }
      if (record.status === channelStatus.reject) {
        return "Rechazado";
      }
      if (record.status === channelStatus.incomplete) {
        return "Incompleto"
      }
      return "Pendiente";
    },
    showAssociated(record) {
      if (record.status === channelStatus.pending) {
        return " - ";
      }

      switch (record.channel_name) {
        case "Telefonica":
          return record?.data_parameters?.phone_number || "-";
        case "WhatsApp":
          return record?.data_parameters?.phone_number || "-";
        case "Messenger":
          return record?.data_parameters?.name || "-";
        case "Chat Inbound":
          return record?.data_parameters?.domain || "-";
        default:
          return record.associate || "-";
      }
    },
    isFacebook(record) {
      return record.channel_name === "FB Messenger";
    },
    isChannelActive(record) {
      return record.status == channelStatus.active;
    },
    isWhatsAppSecuritec(record) {
      return record.provider_name === "Securitec";
    },
    isWebChat(record) {
      return record.channel_name === "Chat Inbound";
    },
    allowEdit(record) {
      return [
        ChannelType.Inbound,
        ChannelType.VoiceInbound,
        ChannelType.Whatsapp,
        ChannelType.WhatsappVoice,
        ChannelType.Email,
      ].includes(record.channel_id);
    },
    isWhatsapp(record) {
      return record.channel_id === ChannelType.Whatsapp;
    },
    isSMS(record) {
      return record.channel_id === ChannelType.Sms;
    },
    isInbound(record) {
      return [
        ChannelType.Inbound,
        ChannelType.VoiceInbound,
      ].includes(record.channel_id);
    },
    handleEdit(record) {
      if (record.status !== channelStatus.active && record.status !== channelStatus.inactive) return;

      if (record.channel_id === ChannelType.Inbound) {
        this.recordInbound = record;
        this.webChatModal = true;
      } else if (record.channel_id === ChannelType.VoiceInbound) {
        this.recordVoice = record;
        this.voiceModal = true;
      } else if (record.channel_id === ChannelType.Email) {
        console.log(record, "record!!!!");
        this.emailModal = true;
        this.emailRecord = record;
      } else {
        this.recordWhatsapp = record;
        this.whatsappModal = true;
      }
    },
    isSendAsDisabled(record) {
      if (record.status !== channelStatus.inactive) {
        return false;
      }
      const aliases = this.sendAsAliasesCache[record.id];
      return !aliases || aliases.length === 0;
    },
    preloadEmailAliases() {
      if (!this.channelsCustomer) return;
      this.channelsCustomer.forEach((record) => {
        if (record.channel_id === ChannelType.Email) {
          this.loadPopoverAliases(record);
        }
      });
    },
    handleSendAsConfig(record) {
      this.sendAsRecord = record;
      this.sendAsModalVisible = true;
    },
    handleSendAsModalClose() {
      this.sendAsModalVisible = false;
      delete this.sendAsAliasesCache[this.sendAsRecord.id];
      this.handleReloadTable();
    },

    async loadPopoverAliases(record) {
      if (this.sendAsAliasesCache[record.id] !== undefined) return;
      this.sendAsAliasesCache[record.id] = null; // Mark as loading
      try {
        const data = await fetchSendAsAliases(record.id);
        this.sendAsAliasesCache[record.id] = Array.isArray(data) ? data : (data?.results || []);
      } catch (error) {
        console.error("Error loading popover aliases:", error);
        delete this.sendAsAliasesCache[record.id]; // Reset to try again on next hover
      }
    },
    handleInfo(value) {
      if (value.channel_id === ChannelType.Inbound) {
        this.recordInbound = value;
        this.webChatModal = true;
      } else if (value.channel_id === ChannelType.VoiceInbound) {
        this.infoRecord = value;
        this.visibleInfoModal = true;
      } else {
        this.infoRecord = value;
        this.visibleInfoModal = true;
      }
    },
    handleWhatsappConfiguration(value) {
      this.whatsappModalConfiguration = true;
      this.$refs.WhatsappFacebookConfigurationRef.setGupshupAppId(value.data_parameters?.app_id  || '');
    },
    refreshWhatsappConfiguration(value) {
      this.whatsappModalConfiguration = false;
      this.handleReloadTable();
    },
    async handleDeleteChannel(value) {
      if (value.status === channelStatus.active) {
        await this.handleValidateChannelBots(value, "delete");
        this.recordDelete = value;
        this.visibleDelete = true;
      } else if (this.isWhatsappMessagePlusVoice(value) && value.status === channelStatus.pending){
        this.recordCancel = value;
        this.visibleCancel = true;
      } else {
        Modal.confirm({
          icon: createVNode(QuestionCircleOutlined),
          wrapClassName: "modal-delete",
          title: this.handleTitle(value),
          content: this.handleContent(value),
          okText: value.status === channelStatus.reject ? "Si, eliminar" : "Si, cancelar",
          cancelText: value.status === channelStatus.reject ? "No, cancelar": "No, mantener solicitud",
          centered: true,
          onOk: async () => {
            try {
              if (value.provider_name === "Inbound") {
                await this.$store.dispatch("deleteInboundChat", value.data_parameters.token);
              }
              await this.$store.dispatch("deleteChannel", value.id);
              
              this.showMessage(value);
              this.handleReloadTable();
            } catch (error) {
              console.error(error);
              const errorMessage = error?.getErrorMessage?.() || "Ha courrido un error";

              message.warning(errorMessage);
            }
          },
        });
      }
    },
    handleTitle(value) {
      if (value.status === channelStatus.pending && [ChannelType.Whatsapp, ChannelType.WhatsappVoice].includes(value.channel_id)) {
        return `Estás a punto de cancelar tu solicitud de ${value.channel_name}, ¿deseas continuar?`
      }
      if (value.status === channelStatus.reject) {
        return this.isWhatsappMessagePlusVoice(value) 
        ? `Estás a punto de eliminar la solicitud rechazada de ${value.channel_name}, ¿deseas continuar?`
        : "Estás a punto de eliminar una solicitud rechazada, ¿deseas continuar?"
      }
      return "Estás a punto de cancelar tu solicitud, ¿deseas continuar?"
    },
    handleContent(value) {
      if (value.status === channelStatus.reject) {
        return this.isWhatsappMessagePlusVoice(value) 
        ? "Recuerda que también se eliminará la solicitud rechazada de WhatsApp Voice asociada al mismo número."
        : "Al eliminar la solicitud rechazada, se borrará su registro de la lista."
      } 
      return "Al cancelar tu solicitud, esta será eliminada del registro."
    },
    async handleErrorModal(title, botIA, chatBot, sequence, ivr) {
      const linkedTypes = [botIA.length > 0, chatBot.length > 0, sequence.length > 0, ivr.length > 0].filter(Boolean).length;
      const text = linkedTypes > 1
          ? "Inactívalos o reemplaza el identificador por otro en su configuración."
          : sequence.length > 0
          ? "Inactiva las secuencias vinculadas o reemplaza el identificador por otro en su configuración."
          : chatBot.length > 0
          ? "Inactiva los chatbots vinculados o reemplaza el identificador por otro en su configuración."
          : ivr.length > 0
          ? "Inactiva el IVR vinculado o reemplaza el identificador por otro en su configuración."
          : "Inactiva los agentes IA vinculados o reemplaza el identificador por otro en su configuración.";

      Modal.confirm({
        title: title,
        icon: createVNode(QuestionCircleOutlined),
        content:  createVNode("div", { class: "inactive-bot-modal" }, [
                  createVNode("p", { class: "text-base font-medium mb-2" }, text),
                  // ChatBots
                  chatBot.length > 0
                    ? createVNode("div", {}, [
                        createVNode(
                          "span",
                          { class: "font-semibold mb-1" },
                          "Chatbots vinculados:"
                        ),
                        createVNode(
                          "ul",
                          { class: "list-disc list-inside text-sm text-gray-600" },
                          chatBot.map((c) => createVNode("li", { key: c }, c))
                        ),
                      ])
                    : null,
                  // Secuencias
                  sequence.length > 0
                    ? createVNode("div", {}, [
                        createVNode(
                          "span",
                          { class: "font-semibold mb-1" },
                          "Secuencias vinculadas:"
                        ),
                        createVNode(
                          "ul",
                          { class: "list-disc list-inside text-sm text-gray-600" },
                          sequence.map((seq) => createVNode("li", { key: seq }, seq))
                        ),
                      ])
                    : null,
                  // IVR
                  ivr.length > 0
                    ? createVNode("div", {}, [
                        createVNode(
                          "span",
                          { class: "font-semibold mb-1" },
                          "IVR vinculado:"
                        ),
                        createVNode(
                          "ul",
                          { class: "list-disc list-inside text-sm text-gray-600" },
                          ivr.map((i) => createVNode("li", { key: i }, i))
                        ),
                      ])
                    : null,
                  // Agentes IA
                  botIA.length > 0
                    ? createVNode("div", {}, [
                        // Bots AI
                        createVNode(
                          "span",
                          { class: "font-semibold mb-1" },
                          "Agentes IA vinculados:"
                        ),
                        createVNode(
                          "ul",
                          { class: "list-disc list-inside text-sm text-gray-600 mb-2" },
                          botIA.map((bot) => createVNode("li", { key: bot }, bot))
                        ),

                        
                      ])
                    : null,
                ]),
                
        centered: true,
        okText: "Entendido",
        cancelButtonProps: { style: { display: "none" } },
      });
    },
    async handleValidateChannelBots(value, action = "delete") {
      try {
        if (value?.id) {
          const response = await fetchValidateChannelBots(value?.id);
        }
      } catch (error) {
        if (error.status === 400) {
          const data = error.data;

          // Ahora puedes acceder a cada valor
          const chatbot_total = Number(data.chatbot_total) || 0;
          const sequence_total = Number(data.sequence_total) || 0;
          const ivr_total = Number(data.ivr_total) || 0;
          const beexai_total = Number(data.beexai_total) || 0;
          const actionText = action === "inactive" ? "inactivarse" : "eliminarse";
          const ending =
            sequence_total > 0 && beexai_total === 0
              ? `${sequence_total > 1 ? "activas" : "activa"}`
              : `${chatbot_total + sequence_total + beexai_total > 1 ? "activos" : "activo"}`;
          const message = [
            chatbot_total > 0 || sequence_total > 0 || ivr_total > 0 || beexai_total > 0
            ? `Este identificador no puede ${actionText} porque está vinculado a ${
                [
                  chatbot_total > 0 ? `${chatbot_total} chatbot${chatbot_total > 1 ? "s" : ""}` : null,
                  sequence_total > 0 ? `${sequence_total} secuencia${sequence_total > 1 ? "s" : ""}` : null,
                  ivr_total > 0 ? `un IVR Operador Automático` : null,
                  beexai_total > 0 ? `${beexai_total} agente${beexai_total > 1 ? "s IA" : " IA"}` : null,
                ].filter(Boolean).join(", ").replace(/, ([^,]*)$/, " y $1")
              } ${ending}.`
            : ""
            ].filter(Boolean).join(" ");

          const chatBot = data.chatbot;
          const sequence = data.sequence;
          const ivr = data.ivr;
          const beexAI = data.beexai;
          await this.handleErrorModal(
            message,
            beexAI,
            chatBot,
            sequence,
            ivr,
          );
        } else {
          this.$message.warning("Algo salió mal, por favor, vuelve a intentarlo.");
        }
        throw error;
      }
    },
    showMessage(value) {
      let textAction = "el identificador";
      if (value.status === channelStatus.reject) {
        textAction = "la solicitud rechazada";
      } else if (value.status === channelStatus.pending) {
        textAction = "la solicitud";
      }

      if (this.isSMS(value)) {
        this.$message.success(
          `Se eliminó con éxito ${textAction} del canal SMS.`
        );
      }
      else if (value.channel_id === ChannelType.Whatsapp){
        if (value.data_parameters.enable_voice) {
          this.$message.success(
            `Se eliminó con éxito ${textAction} de WhatsApp Message y Voice.`
          );
        } else {
          this.$message.success(
            `Se eliminó con éxito ${textAction} de WhatsApp Message.`
          );
        }
      }
      else if (value.channel_id === ChannelType.WhatsappVoice){
        this.$message.success(
          `Se eliminó con éxito ${textAction} de WhatsApp Voice.`
        );
      }
      else if (value.status === channelStatus.reject) {
        this.$message.success(
          `Se eliminó con éxito la solicitud rechazada del número entrante.`
        );
      }
      else {
        this.$message.success(
          `Se eliminó ${textAction} de ${value.provider_name} asociado a ${value.associate}.`
        );
      }
    },
    async handleStatusChange(item) {
      
      if (item.status !== channelStatus.pending && item.status !== channelStatus.reject) {
        if (item.status == channelStatus.inactive) {
          if (item.channel_id === ChannelType.WhatsappVoice && this.isBlockByWhatsappVoiceBalance()) {
            this.handleInactiveError();
            return;
          } 
          const data = {
            id: item.id,
            status: channelStatus.active,
          };

          Modal.confirm({
            title: this.handleTitleActiveModal(item),
            content: this.handleContentActiveModal(item),
            onOk: async () => {
              try {
                if (item.channel_id === 6 && item.data_parameters) {
                  await Promise.all([
                    fbSubscribeApps({
                      pageId: item.data_parameters.id,
                      pageAccessToken: item.data_parameters.token,
                    }),
                    this.$store.dispatch("updateChannelCreate", data),
                  ]);
                } else {
                  await this.$store.dispatch("updateChannelCreate", data);
                }
                this.showMessageActive(item);
                this.handleReloadTable();
              } catch (error) {
                const errorMessage = error?.getErrorMessage() || "Ha courrido un error";

                message.warning(errorMessage);
              }
            },
            icon: createVNode(QuestionCircleOutlined),
            centered: true,
            okText: this.handleOkText(item),
            cancelText: item.channel_id === ChannelType.WhatsappVoice ? "No, cancelar" : "Cancelar",
          });
        } else {
          await this.handleValidateChannelBots(item, "inactive");
          this.recordDeactivate = item;
          this.visibleDeactivate = true;
        }
      }
    },
    handleTitleActiveModal(item) {
      if(item.channel_id === ChannelType.WhatsappVoice) {
        return this.findMessageForVoice(item)  
        ? `Para activar el identificador de WhatsApp Voice “${item.associate}”, se activará el WhatsApp Message asociado al mismo número, ¿deseas continuar?`
        : `Estás a punto de activar el identificador de WhatsApp Voice, ¿deseas continuar?`
      }
      return `¿Estás seguro de activar este canal?`
    },

    handleContentActiveModal(item) {
      if(item.channel_id === ChannelType.WhatsappVoice) {
        return this.findMessageForVoice(item)  
        ? ``
        : `Al activar este identificador, podrás volver a configurar la recepción de llamadas desde WhatsApp.`
      }
      return `Recuerda asociar este canal a una o varias colas para atender tus interacciones.`
    },

    handleOkText(item) {
      if(item.channel_id === ChannelType.WhatsappVoice) {
        return this.findMessageForVoice(item)  
        ? `Si, activar ambos`
        : `Si, activar`
      }
      return `Activar`
    },

    showMessageActive(item) {
      if(item.channel_id === ChannelType.WhatsappVoice) {
        if (this.findMessageForVoice(item)) {
          this.$message.success(
            `El identificador de WhatsApp Message y Voice “${item.associate}” fue activado.`
          );
        } else {
          this.$message.success(
            `El identificador de WhatsApp Voice “${item.associate}” fue activado.`
          );
        }
      }
      else if((item.channel_id === ChannelType.Whatsapp)) {
        this.$message.success(
          `El identificador de WhatsApp Message “${item.associate}” fue activado.`
        );
      }
      else {
        this.$message.success(
          `Se activó el canal de ${item.provider_name} asociado a ${this.showAssociated(item)}.`
        );
      }  
    },

    handleReloadTable() {
      setTimeout(() => {
        this.$emit("reloadTable");
      }, "10");
    },
    handleUnLinkFromFacebook(item) {
      const data = {
        id: item.id,
        status: item.status == channelStatus.active ? channelStatus.inactive : channelStatus.active,
      };
      Modal.confirm({
        title: "¿Estás seguro de desvincular este canal?",
        content: "Recuerda que las interacciones asociadas a este canal serán finalizadas.",
        icon: createVNode(QuestionCircleOutlined),
        onOk: () => {
          if (item.page) {
            fbUnlinkPage({
              pageId: item.page.id,
              pageAccessToken: item.page.token,
            });
          }
          try {
            this.$store.dispatch("updateChannelCreate", data);
          } catch (error) {
            message.warning("Algo salió mal, por favor, vuelve a intentarlo");
          }
        },
        centered: true,
        okText: "Desvincular",
        cancelText: "Cancelar",
      });
    },
    handleLinkWithFacebook(item) {
      const obj = {
        id: item.id,
        status: item.status == channelStatus.active ? channelStatus.inactive : channelStatus.active,
      };
      Modal.confirm({
        title: "¿Estás seguro de Vincular este canal?",
        icon: createVNode(QuestionCircleOutlined),
        content: "Recuerda que las interacciones asociadas a este canal serán finalizadas.",
        onOk: () => {
          if (item.page) {
            fbSubscribeApps({
              pageId: item.page.id,
              pageAccessToken: item.page.token,
            });
          }
          try {
            this.$store.dispatch("updateChannelCreate", obj);
          } catch (error) {
            message.warning("Algo salió mal, por favor, vuelve a intentarlo");
          }
        },
        centered: true,
        okText: "Vincular",
        cancelText: "Cancelar",
      });
    },
    handleCopyError() {
      this.$message.error("Falló");
    },
    handleInactiveError() {
      this.$message.error("Saldo insuficiente. Recarga el canal de WhatsApp Voice para activarlo.");
    },
    handleCopySuccess() {
      this.$message.success("Copiado");
    },
    goToAddTemplate(record) {
      let channelTemplatesPath = "";

      switch (record.channel_id) {
        case ChannelType.Whatsapp:
          channelTemplatesPath = `whatsapp-templates/${record?.id}/?associate=${record?.associate}`;
          break;

        case ChannelType.Sms:
          channelTemplatesPath = `sms-templates/${record?.id}/`;
          break;

        default:
          this.$message.error("Este canal no puede tener plantillas");
      }

      this.$router.push(`/channels/board/${channelTemplatesPath}`);
    },
    async handleLinkWithWhatsApp(record){
      try {
        const response = await fetchMmLite(record.id);
        await this.$store.dispatch("updateChannelCreate", {id: record.id, enable_mm_lite: true});
        this.handleReloadTable();
        window.open(response?.mm_lite?.link, "_blank");        
      } catch (error) {
        const errorMessage = error?.getCleanErrorMessage() || `Ha ocurrido un error`;
        message.warning(errorMessage);
      }
    },
    handleEnableVoice(record) {
      if (this.plansWhatsappVoice) {
        this.recordWhatsappVoice = record;
        this.visibleWhatsappVoice = true;
      }
      else {
        this.openRestriction = true;
      }
    },
    async fetchWhatsappVoiceBalance() {
      const response = await fetchWhatsappBalance("whatsapp-voice");

      this.balance_voice = response.balance;
      this.type_billing_voice = response.type_billing;
    },
    isBlockByWhatsappVoiceBalance() {
      return (
        this.balance_voice <= 0 &&
        this.type_billing_voice === TypeBilling.PrePayment
      );
    },
    ActiveWhatsappVoice(record) {
      return (
        !record.data_parameters?.enable_voice && 
        this.isWhatsapp(record) && 
        (record.status === channelStatus.active || record.status === channelStatus.inactive)
      );
    },
  },
});
</script>

<style lang="sass" scoped>
:deep(.ant-table-thead > tr > th .ant-table-column-sorter)
  display: none

:deep(.ant-table-thead > tr > th)
  // padding: 10px !important

.icon--edit
  color: $blue-6

.copy-icon
  font-size: 16px
  margin-left: 8px

.channel
  display: flex
  align-items: center
  position: relative
  &__logo
    display: block
    width: 23px
    height: 23px
    background-size: cover
    background-position: center
    border-radius: 2px
    margin-right: 12px
  &--empty
    background: linear-gradient(to right, #ccc, #777)

.pop-over
  width: 230px !important

.button
  padding: 0 12px
  width: 100%
  text-align: left

.info
  color: $gray-7
  font-style: italic

.button__table
  font-size: 12px
  padding: 0
  display: flex
  align-items: center
  gap: 4px

  img
    height: 14px
    width: 14px

:deep(.ant-table-row-level-0 > td)
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis
:global(.modal-delete .ant-modal-confirm-body)
  align-items: flex-start !important
:global(.modal-delete .anticon)
  margin-top: 3px !important

.btn-disabled
  color: #00000040 !important
  cursor: not-allowed
  background: transparent !important
  box-shadow: none !important

</style>

<style lang="sass">
.send-as-popover-content
  padding: 4px 6px

  .send-as-title
    font-weight: 600
    font-size: 13px
    margin-bottom: 8px
    color: rgba(0, 0, 0, 0.85)

  .send-as-list
    list-style: none
    padding: 0
    margin: 0
    display: flex
    flex-direction: column
    gap: 6px

    .send-as-item
      display: flex
      align-items: center
      gap: 8px
      font-size: 13px
      color: rgba(0, 0, 0, 0.65)

      .bullet
        color: rgba(0, 0, 0, 0.45)
        font-weight: bold

      .email-text
        display: inline-block
        max-width: 190px
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis
        cursor: help
</style>
